import { useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../lib/api-client';

type ReportPeriod = 'TODAY' | 'WEEK' | 'MONTH' | 'CUSTOM';
type PaymentMethod = 'CASH' | 'QRIS' | 'TRANSFER';
type StockStatus = 'SAFE' | 'LOW_STOCK' | 'OUT_OF_STOCK';

type Outlet = { id: string; code: string; name: string };

type OperationalDashboardData = {
  generatedAt: string;
  timezone: 'Asia/Jakarta';
  scope: { type: 'OUTLET' | 'CONSOLIDATED'; outlets: Outlet[] };
  period: { type: ReportPeriod; from: string; to: string; label: string };
  summary: {
    revenue: number;
    grossProfit: number;
    transactions: number;
    productsSold: number;
    inventoryValue: number;
    inventoryProducts: number;
    stockAttention: number;
    lowStock: number;
    outOfStock: number;
  };
  stockOperations: {
    receipts: number;
    receiptValue: number;
    supplierReturns: number;
    supplierReturnValue: number;
    adjustments: number;
  };
  trend: {
    bucket: 'HOUR' | 'DAY' | 'WEEK' | 'MONTH';
    data: Array<{ label: string; revenue: number; grossProfit: number; transactions: number }>;
  };
  bestSellers: Array<{
    productId: string;
    sku: string;
    name: string;
    quantity: number;
    revenue: number;
    grossProfit: number;
  }>;
  lowStockItems: Array<{
    outlet: Outlet | null;
    product: { id: string; sku: string; name: string };
    quantity: number;
    reserved: number;
    available: number;
    minimum: number;
    averageUnitCost: number;
    value: number;
    status: StockStatus;
  }>;
  recentTransactions: Array<{
    id: string;
    number: string;
    source: 'READY_STOCK' | 'CUSTOM_ORDER';
    customer: string;
    paymentMethod: PaymentMethod;
    total: number;
    grossProfit: number;
    status: string;
    date: string;
    outlet: Outlet | null;
  }>;
  recentInventoryMovements: Array<{
    id: string;
    type: string;
    quantity: number;
    balanceBefore: number | null;
    balanceAfter: number | null;
    reference: string | null;
    reason: string | null;
    createdAt: string;
    product: { sku: string; name: string };
    outlet: Outlet | null;
  }>;
  outletPerformance: Array<{
    outlet: Outlet;
    revenue: number;
    grossProfit: number;
    transactions: number;
    productsSold: number;
    inventoryValue: number;
    lowStock: number;
  }>;
};

const currency = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});
const compactCurrency = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const dateTime = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  dateStyle: 'medium',
  timeStyle: 'short',
});
const periodLabels: Record<ReportPeriod, string> = {
  TODAY: 'Hari Ini',
  WEEK: 'Minggu Ini',
  MONTH: 'Bulan Ini',
  CUSTOM: 'Rentang Tanggal',
};
const paymentLabels: Record<PaymentMethod, string> = {
  CASH: 'Tunai',
  QRIS: 'QRIS',
  TRANSFER: 'Transfer',
};
const movementLabels: Record<string, string> = {
  OPENING_STOCK: 'Stok awal',
  STOCK_RECEIPT: 'Penerimaan barang',
  GOODS_RECEIPT: 'Penerimaan barang',
  SALE: 'Penjualan',
  STOCK_ADJUSTMENT_IN: 'Penyesuaian masuk',
  STOCK_ADJUSTMENT_OUT: 'Penyesuaian keluar',
  STOCK_OPNAME: 'Stok opname',
  RETURN_TO_SUPPLIER: 'Retur supplier',
  RECEIPT_CANCELLATION: 'Pembatalan penerimaan',
};

function today() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function OperationalDashboard({
  consolidated,
  outletReady,
  refreshKey,
  userName,
}: {
  consolidated: boolean;
  outletReady: boolean;
  refreshKey: number;
  userName: string;
}) {
  const currentDate = today();
  const [period, setPeriod] = useState<ReportPeriod>('TODAY');
  const [from, setFrom] = useState(currentDate);
  const [to, setTo] = useState(currentDate);
  const [data, setData] = useState<OperationalDashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function queryString() {
    const params = new URLSearchParams({ period });
    if (period === 'CUSTOM') {
      params.set('from', from);
      params.set('to', to);
    }
    return params.toString();
  }

  async function load() {
    if (!outletReady) return;
    if (period === 'CUSTOM' && (!from || !to || from > to)) {
      setError('Rentang tanggal belum valid.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const path = consolidated ? '/dashboard/operational/consolidated' : '/dashboard/operational';
      setData(await apiRequest<OperationalDashboardData>(`${path}?${queryString()}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Dashboard operasional gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [consolidated, outletReady, period, refreshKey]);

  if (!outletReady) return null;

  const scopeLabel = data
    ? data.scope.type === 'CONSOLIDATED'
      ? `Semua outlet (${data.scope.outlets.length})`
      : (data.scope.outlets[0]?.name ?? 'Outlet aktif')
    : consolidated
      ? 'Semua outlet'
      : 'Outlet aktif';

  return (
    <section className="page-shell" aria-labelledby="operational-dashboard-title">
      <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Halo, {userName.split(' ')[0]}</p>
          <h1
            id="operational-dashboard-title"
            className="mt-1 text-3xl font-black tracking-tight text-slate-950"
          >
            Dashboard Operasional
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Satu ringkasan dari transaksi, inventory, dan aktivitas stok ERP.
          </p>
        </div>
        <div className="surface-card px-4 py-3 text-sm">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Cakupan</p>
          <p className="mt-1 font-black text-slate-800">{scopeLabel}</p>
        </div>
      </div>

      <div className="filter-bar mt-6">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Periode dashboard">
          {(Object.keys(periodLabels) as ReportPeriod[]).map((item) => (
            <button
              key={item}
              type="button"
              className={`rounded-xl px-4 py-2.5 text-sm font-bold ${
                period === item
                  ? 'bg-ajisai-700 text-white'
                  : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
              onClick={() => setPeriod(item)}
            >
              {periodLabels[item]}
            </button>
          ))}
        </div>
        {period === 'CUSTOM' && (
          <form
            className="mt-4 grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              void load();
            }}
          >
            <label className="text-sm font-bold text-slate-700">
              Tanggal Awal
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
                type="date"
                max={to}
                required
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="text-sm font-bold text-slate-700">
              Tanggal Akhir
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
                type="date"
                min={from}
                max={currentDate}
                required
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
            <button className="self-end rounded-xl bg-slate-900 px-5 py-2.5 font-bold text-white">
              Terapkan
            </button>
          </form>
        )}
      </div>

      {error && (
        <div role="alert" className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-bold text-red-800">{error}</p>
          <button
            type="button"
            className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-sm font-bold text-red-700"
            onClick={() => void load()}
          >
            Coba lagi
          </button>
        </div>
      )}

      {loading && !data ? (
        <DashboardSkeleton />
      ) : data ? (
        <DashboardContent data={data} loading={loading} />
      ) : null}
    </section>
  );
}

function DashboardContent({ data, loading }: { data: OperationalDashboardData; loading: boolean }) {
  const cards = [
    ['Omzet', currency.format(data.summary.revenue), 'Transaksi selesai pada periode aktif'],
    ['Laba Kotor', currency.format(data.summary.grossProfit), 'Menggunakan snapshot HPP transaksi'],
    [
      'Transaksi',
      data.summary.transactions.toLocaleString('id-ID'),
      'Ready-stock + Pesanan Custom',
    ],
    ['Produk Terjual', data.summary.productsSold.toLocaleString('id-ID'), 'Produk ready-stock'],
    [
      'Nilai Inventory',
      currency.format(data.summary.inventoryValue),
      `${data.summary.inventoryProducts} posisi stok`,
    ],
    [
      'Perlu Perhatian',
      data.summary.stockAttention.toLocaleString('id-ID'),
      `${data.summary.lowStock} menipis · ${data.summary.outOfStock} habis`,
    ],
  ];

  return (
    <div className={loading ? 'opacity-60 transition' : 'transition'} aria-busy={loading}>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>
          {data.period.label}: {data.period.from} s.d. {data.period.to}
        </span>
        <span>Diperbarui {dateTime.format(new Date(data.generatedAt))} WIB</span>
      </div>

      <section className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {cards.map(([label, value, note]) => (
          <article
            key={label}
            className="surface-card p-5"
          >
            <p className="text-sm font-semibold text-slate-500">{label}</p>
            <p className="mt-5 break-words text-2xl font-black tracking-tight text-slate-950">
              {value}
            </p>
            <p className="mt-2 text-xs leading-5 text-slate-400">{note}</p>
          </article>
        ))}
      </section>

      <div className="mt-8 grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <TrendPanel trend={data.trend} />
        <StockOperations data={data.stockOperations} />
      </div>

      {data.scope.type === 'CONSOLIDATED' && <OutletPerformance rows={data.outletPerformance} />}

      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <BestSellers rows={data.bestSellers} />
        <LowStock rows={data.lowStockItems} />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <RecentTransactions rows={data.recentTransactions} />
        <RecentMovements rows={data.recentInventoryMovements} />
      </div>
    </div>
  );
}

function TrendPanel({ trend }: { trend: OperationalDashboardData['trend'] }) {
  const max = Math.max(1, ...trend.data.flatMap((item) => [item.revenue, item.grossProfit]));
  const width = 800;
  const height = 230;
  const revenue = trend.data
    .map(
      (item, index) =>
        `${pointX(index, trend.data.length, width)},${pointY(item.revenue, max, height)}`,
    )
    .join(' ');
  const profit = trend.data
    .map(
      (item, index) =>
        `${pointX(index, trend.data.length, width)},${pointY(item.grossProfit, max, height)}`,
    )
    .join(' ');
  const hasActivity = trend.data.some((item) => item.transactions > 0);

  return (
    <article className="surface-card p-5 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-ajisai-700">Tren Periode Aktif</p>
          <h2 className="mt-1 text-xl font-black">Omzet dan laba kotor</h2>
        </div>
        <div className="flex gap-4 text-xs font-bold text-slate-500">
          <span>
            <i className="mr-1 inline-block size-2 rounded-full bg-ajisai-600" />
            Omzet
          </span>
          <span>
            <i className="mr-1 inline-block size-2 rounded-full bg-emerald-500" />
            Laba
          </span>
        </div>
      </div>
      {hasActivity ? (
        <div className="mt-5 overflow-hidden rounded-xl bg-slate-50 p-3">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="h-56 w-full"
            role="img"
            aria-label="Grafik omzet dan laba kotor"
          >
            {[0.25, 0.5, 0.75].map((ratio) => (
              <line
                key={ratio}
                x1="0"
                x2={width}
                y1={height * ratio}
                y2={height * ratio}
                stroke="#e7dfda"
                strokeDasharray="6 6"
              />
            ))}
            <polyline
              points={revenue}
              fill="none"
              stroke="#a86f6f"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points={profit}
              fill="none"
              stroke="#10b981"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="mt-1 flex justify-between text-[10px] text-slate-400">
            <span>{trend.data[0]?.label}</span>
            <span>{trend.data.at(-1)?.label}</span>
          </div>
        </div>
      ) : (
        <EmptyState text="Belum ada transaksi selesai pada periode ini." />
      )}
    </article>
  );
}

function StockOperations({ data }: { data: OperationalDashboardData['stockOperations'] }) {
  const rows = [
    ['Penerimaan Barang', data.receipts, currency.format(data.receiptValue)],
    ['Retur Supplier', data.supplierReturns, currency.format(data.supplierReturnValue)],
    ['Penyesuaian Stok', data.adjustments, 'movement pada periode aktif'],
  ] as const;
  return (
    <article className="surface-card p-5 sm:p-6">
      <p className="text-sm font-bold text-ajisai-700">Operasional Stok</p>
      <h2 className="mt-1 text-xl font-black">Aktivitas periode aktif</h2>
      <div className="mt-5 space-y-3">
        {rows.map(([label, count, note]) => (
          <div key={label} className="flex items-center justify-between rounded-xl bg-slate-50 p-4">
            <div>
              <p className="font-bold text-slate-800">{label}</p>
              <p className="mt-1 text-xs text-slate-500">{note}</p>
            </div>
            <strong className="text-2xl text-ajisai-800">{count}</strong>
          </div>
        ))}
      </div>
    </article>
  );
}

function OutletPerformance({ rows }: { rows: OperationalDashboardData['outletPerformance'] }) {
  return (
    <article className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <PanelHeader eyebrow="Konsolidasi" title="Performa per outlet" />
      {rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Outlet</th>
                <th className="px-5 py-3 text-right">Omzet</th>
                <th className="px-5 py-3 text-right">Laba</th>
                <th className="px-5 py-3 text-right">Transaksi</th>
                <th className="px-5 py-3 text-right">Inventory</th>
                <th className="px-5 py-3 text-right">Stok kritis</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, index) => (
                <tr key={row.outlet.id}>
                  <td className="px-5 py-4">
                    <span className="mr-3 inline-grid size-7 place-items-center rounded-lg bg-ajisai-50 text-xs font-black text-ajisai-700">
                      {index + 1}
                    </span>
                    <strong>{row.outlet.name}</strong>
                    <span className="ml-2 text-xs text-slate-400">{row.outlet.code}</span>
                  </td>
                  <td className="px-5 py-4 text-right font-bold">{currency.format(row.revenue)}</td>
                  <td className="px-5 py-4 text-right text-emerald-700">
                    {currency.format(row.grossProfit)}
                  </td>
                  <td className="px-5 py-4 text-right">{row.transactions}</td>
                  <td className="px-5 py-4 text-right">{currency.format(row.inventoryValue)}</td>
                  <td className="px-5 py-4 text-right">{row.lowStock}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState text="Belum ada outlet aktif." />
      )}
    </article>
  );
}

function BestSellers({ rows }: { rows: OperationalDashboardData['bestSellers'] }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <PanelHeader eyebrow="Produk" title="Produk terlaris" />
      {rows.length ? (
        <div className="divide-y divide-slate-100">
          {rows.map((row, index) => (
            <div key={row.productId} className="flex items-center gap-4 px-5 py-4">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-ajisai-50 font-black text-ajisai-700">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{row.name}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {row.sku} · {row.quantity} terjual
                </p>
              </div>
              <div className="text-right">
                <p className="font-black">{compactCurrency.format(row.revenue)}</p>
                <p className="mt-1 text-xs text-emerald-600">
                  Laba {compactCurrency.format(row.grossProfit)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState text="Belum ada produk terjual pada periode ini." />
      )}
    </article>
  );
}

function LowStock({ rows }: { rows: OperationalDashboardData['lowStockItems'] }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <PanelHeader eyebrow="Inventory" title="Stok perlu perhatian" />
      {rows.length ? (
        <div className="divide-y divide-slate-100">
          {rows.map((row) => (
            <div
              key={`${row.outlet?.id}-${row.product.id}`}
              className="flex items-center gap-4 px-5 py-4"
            >
              <span
                className={`h-9 w-1 shrink-0 rounded-full ${row.status === 'OUT_OF_STOCK' ? 'bg-red-500' : 'bg-amber-500'}`}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{row.product.name}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {row.product.sku}
                  {row.outlet ? ` · ${row.outlet.name}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="font-black">{row.available} tersedia</p>
                <p className="mt-1 text-xs text-slate-500">Minimum {row.minimum}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState text="Semua stok berada pada kondisi aman." />
      )}
    </article>
  );
}

function RecentTransactions({ rows }: { rows: OperationalDashboardData['recentTransactions'] }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <PanelHeader eyebrow="Transaksi" title="Transaksi terbaru" />
      {rows.length ? (
        <div className="divide-y divide-slate-100">
          {rows.map((row) => (
            <div key={`${row.source}-${row.id}`} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate font-bold text-ajisai-800">{row.number}</p>
                  <p className="mt-1 truncate text-sm text-slate-600">{row.customer}</p>
                </div>
                <p className="whitespace-nowrap font-black">{currency.format(row.total)}</p>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                <span>{row.source === 'CUSTOM_ORDER' ? 'Pesanan Custom' : 'Ready-stock'}</span>
                <span>{paymentLabels[row.paymentMethod]}</span>
                {row.outlet && <span>{row.outlet.name}</span>}
                <span>{dateTime.format(new Date(row.date))}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState text="Belum ada transaksi selesai pada periode ini." />
      )}
    </article>
  );
}

function RecentMovements({ rows }: { rows: OperationalDashboardData['recentInventoryMovements'] }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <PanelHeader eyebrow="Inventory" title="Pergerakan stok terbaru" />
      {rows.length ? (
        <div className="divide-y divide-slate-100">
          {rows.map((row) => (
            <div key={row.id} className="flex gap-4 px-5 py-4">
              <span
                className={`mt-1 grid size-8 shrink-0 place-items-center rounded-lg text-sm font-black ${row.quantity >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
              >
                {row.quantity >= 0 ? '+' : '−'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold">{row.product.name}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {movementLabels[row.type] ?? row.type.replaceAll('_', ' ')}
                    </p>
                  </div>
                  <strong className={row.quantity >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                    {row.quantity > 0 ? '+' : ''}
                    {row.quantity}
                  </strong>
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  {row.reference ?? 'Tanpa referensi'}
                  {row.outlet ? ` · ${row.outlet.name}` : ''} ·{' '}
                  {dateTime.format(new Date(row.createdAt))}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState text="Belum ada pergerakan stok pada periode ini." />
      )}
    </article>
  );
}

function PanelHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="border-b border-slate-200 px-5 py-4">
      <p className="text-xs font-bold uppercase tracking-wide text-ajisai-700">{eyebrow}</p>
      <h2 className="mt-1 text-lg font-black">{title}</h2>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="px-5 py-12 text-center">
      <p className="mx-auto max-w-sm text-sm font-semibold leading-6 text-slate-500">{text}</p>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="mt-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="h-36 animate-pulse rounded-2xl bg-slate-200" />
        ))}
      </div>
      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <div className="h-80 animate-pulse rounded-2xl bg-slate-200" />
        <div className="h-80 animate-pulse rounded-2xl bg-slate-200" />
      </div>
    </div>
  );
}

function pointX(index: number, length: number, width: number) {
  return length <= 1 ? width / 2 : (index / (length - 1)) * width;
}

function pointY(value: number, max: number, height: number) {
  return height - 18 - (value / max) * (height - 36);
}
